/**
 * The shortage card (M8 plan MC-c4): a missing fab input names where to get it, with price and age ("never a wall,
 * sometimes a trip"). Pure over cached station views, the pilot's price log (TR-1a `knownPrice`) and the minerals
 * knowledge (MC-a7a). Rare (UI reads): allocates freely.
 *
 * - **Jumps** are hops over the relays online now (`isRelayOnline`: the Reach state over the generator flag) from
 *   the pilot's system; a system no online route reaches is never listed.
 * - **Prices** are live only in covered systems (RD9: the current system and systems with a built player-corp
 *   station, the caller's `covered` set, TR-1b's rule); elsewhere they are the logged system best with its age. A
 *   system with no live view and no log entry has no data and is not listed.
 * - **A buyable input:** sellers within `strategic/roles.json supplier.jumpRange` (2) jumps.
 * - **The carry** (plan decision 3): (i) sellers anywhere with a known price (none for platinum today); (ii) mining
 *   sources: charted systems whose signature carries the ore, then those whose deposits merely carry it, each by
 *   jumps; (iii) when no charted signature source is known, the nearest rumoured system of a region whose signature
 *   list (`galaxy.json minerals.signature.eligible`) holds the ore: what to chart. A component carry (a data core)
 *   has no market and no ore, so it gets no rows (it is salvaged).
 * - Rows go sellers, signature sources, the chart row, then the other deposits, cut at
 *   `modules.json materials.shortage.maxListed`.
 */
import type { Career, FabCarryId } from '../career/types'
import { galaxyGradeIds, loadGalaxyConfig, type GalaxyGradeId } from '../data/galaxyConfig'
import type { M2Data } from '../data/m2Schemas'
import rolesJson from '../data/strategic/roles.json'
import { mineralsKnowledge } from '../exploration/minerals'
import { bfsHops } from '../galaxy/relays'
import type { Galaxy, MineralOre, RegionId } from '../galaxy/types'
import { isRelayOnline } from '../reach/state'
import type { ReachState } from '../reach/types'
import { isWareId, type WareId as StrategicWareId } from '../strategic/wares'
import { knownPrice, type KnownPrice, type PriceSource } from '../trade-intel/priceLog'

const INPUT_JUMPS: number = rolesJson.supplier.jumpRange
const MINERAL_ORES: readonly string[] = ['ferrite', 'silicates', 'ice', 'titanium', 'platinum'] satisfies readonly MineralOre[]

/** One row of the card: a seller, a mining source, or a system to chart. */
export interface ShortageRow {
  kind: 'seller' | 'mining' | 'chart'
  systemId: string
  /** The selling station; null for mining and chart rows, or when no cached view names a logged seller. */
  stationId: string | null
  jumps: number
  /** Unit price the pilot pays (whole credits), or null for mining and chart rows. */
  unit: number | null
  /** Age of the price in faction days (0 = live), or null for mining and chart rows. */
  ageDays: number | null
  /** The price is read live (a covered system), not from the log. */
  live: boolean
  /** Mining rows: the richest charted grade carrying the ore (null: carried by the signature only). */
  grade: GalaxyGradeId | null
}

/** What the card reads besides the Career: the Reach, its galaxy and the cached system views. */
export interface ShortageWorld {
  reach: ReachState
  galaxy: Galaxy
  /** Cached system views (any age). A covered system's view is live; others only name a logged price's station. */
  views: readonly PriceSource[]
  /** The covered systems (TR-1b): the current one and those with a built player-corp station. */
  covered: ReadonlySet<string>
}

export interface ShortageAsk {
  /** The pilot's system (jumps count from here). */
  systemId: string
  /** The docked station, left out of the sellers (its own stock is the fab quote's `buyMissing`); null = none. */
  stationId: string | null
  item: FabCarryId
  /** 'input': a buyable recipe input; 'carry': the recipe's carry (plan decision 3). */
  role: 'input' | 'carry'
}

let cachedEligible: Readonly<Record<RegionId, readonly string[]>> | null = null
function signatureEligible(): Readonly<Record<RegionId, readonly string[]>> {
  cachedEligible ??= loadGalaxyConfig().minerals.signature.eligible
  return cachedEligible
}

function isMineralOre(id: string): id is MineralOre {
  return MINERAL_ORES.includes(id)
}

function cmp(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0
}

function gradeRank(g: GalaxyGradeId | null): number {
  return g === null ? -1 : galaxyGradeIds.indexOf(g)
}

/** Online relay hops from `systemId` per system id (absent = unreachable). Empty when the galaxy lacks it. */
function onlineHops(world: ShortageWorld, systemId: string): Map<string, number> {
  const { galaxy, reach } = world
  const index = new Map(galaxy.systems.map((s, i) => [s.id, i]))
  const out = new Map<string, number>()
  const from = index.get(systemId)
  if (from === undefined) return out
  const edges: [number, number][] = []
  for (const r of galaxy.relays) {
    const a = index.get(r.a)
    const b = index.get(r.b)
    if (a !== undefined && b !== undefined && isRelayOnline(reach, galaxy, r.id)) edges.push([a, b])
  }
  const hops = bfsHops(galaxy.systems.length, edges, from)
  for (let i = 0; i < hops.length; i++) if (hops[i] >= 0) out.set(galaxy.systems[i].id, hops[i])
  return out
}

/** The cheapest live seller in a covered system's view, leaving out station `exclude`. */
function liveSeller(view: PriceSource, career: Career, ware: StrategicWareId, exclude: string | null): KnownPrice | null {
  let best: KnownPrice | null = null
  for (let i = 0; i < view.stations.length; i++) {
    if (view.stations[i].id === exclude) continue
    const p = knownPrice(view, career, view.systemId, ware, 'buy', i)
    if (p !== null && (best === null || p.unit < best.unit)) best = p
  }
  return best
}

/** Sellers of `ware` within `maxJumps`, nearest first, then cheapest. */
function sellerRows(
  career: Career,
  world: ShortageWorld,
  ask: ShortageAsk,
  ware: StrategicWareId,
  hops: ReadonlyMap<string, number>,
  maxJumps: number,
): ShortageRow[] {
  const views = new Map(world.views.map((v) => [v.systemId, v]))
  const systems = new Set<string>([...Object.keys(career.priceLog ?? {}), ...world.covered])
  const rows: ShortageRow[] = []
  for (const systemId of systems) {
    const jumps = hops.get(systemId)
    if (jumps === undefined || jumps > maxJumps) continue
    const view = views.get(systemId)
    const covered = view !== undefined && world.covered.has(systemId)
    const price = covered ? liveSeller(view, career, ware, ask.stationId) : knownPrice(null, career, systemId, ware, 'buy')
    if (price === null) continue
    const stationId = view?.stations[price.station]?.id ?? null
    if (stationId !== null && stationId === ask.stationId) continue
    rows.push({ kind: 'seller', systemId, stationId, jumps, unit: price.unit, ageDays: price.ageDays, live: price.live, grade: null })
  }
  return rows.sort((a, b) => a.jumps - b.jumps || (a.unit ?? 0) - (b.unit ?? 0) || cmp(a.systemId, b.systemId))
}

/** Charted mining sources of `ore`: [signature systems, systems whose deposits only carry it], each by jumps. */
function miningRows(world: ShortageWorld, ore: MineralOre, hops: ReadonlyMap<string, number>): [ShortageRow[], ShortageRow[]] {
  const signature: ShortageRow[] = []
  const deposit: ShortageRow[] = []
  for (const [systemId, jumps] of hops) {
    const k = mineralsKnowledge(world.reach, world.galaxy, systemId)
    if (k === 'uncharted') continue
    const grade = k.grades[ore] ?? null
    const bySignature = k.signature !== null && (k.signature.primary === ore || k.signature.secondary === ore)
    if (!bySignature && grade === null) continue
    const row: ShortageRow = { kind: 'mining', systemId, stationId: null, jumps, unit: null, ageDays: null, live: false, grade }
    if (bySignature) signature.push(row)
    else deposit.push(row)
  }
  const order = (a: ShortageRow, b: ShortageRow): number =>
    a.jumps - b.jumps || gradeRank(b.grade) - gradeRank(a.grade) || cmp(a.systemId, b.systemId)
  return [signature.sort(order), deposit.sort(order)]
}

/** The nearest rumoured system of a region whose signature list holds `ore` (what to chart), or null. */
function chartRow(world: ShortageWorld, ore: MineralOre, hops: ReadonlyMap<string, number>): ShortageRow | null {
  const eligible = signatureEligible()
  let best: ShortageRow | null = null
  for (const s of world.galaxy.systems) {
    if (world.reach.knowledge[s.id] !== 'rumoured' || !eligible[s.region].includes(ore)) continue
    const jumps = hops.get(s.id)
    if (jumps === undefined) continue
    if (best === null || jumps < best.jumps || (jumps === best.jumps && cmp(s.id, best.systemId) < 0)) {
      best = { kind: 'chart', systemId: s.id, stationId: null, jumps, unit: null, ageDays: null, live: false, grade: null }
    }
  }
  return best
}

/**
 * Where to get `ask.item`: sellers (with price and age), mining sources and what to chart, at most
 * `materials.shortage.maxListed` rows (every row when the data block is absent). Empty when nothing is known.
 */
export function shortageRows(career: Career, data: Pick<M2Data, 'modules'>, world: ShortageWorld, ask: ShortageAsk): ShortageRow[] {
  const hops = onlineHops(world, ask.systemId)
  const rows: ShortageRow[] = []
  if (isWareId(ask.item)) rows.push(...sellerRows(career, world, ask, ask.item, hops, ask.role === 'input' ? INPUT_JUMPS : Infinity))
  if (ask.role === 'carry' && isMineralOre(ask.item)) {
    const [signature, deposit] = miningRows(world, ask.item, hops)
    rows.push(...signature)
    if (signature.length === 0) {
      const chart = chartRow(world, ask.item, hops)
      if (chart !== null) rows.push(chart)
    }
    rows.push(...deposit)
  }
  const max = data.modules.materials?.shortage.maxListed
  return max === undefined ? rows : rows.slice(0, max)
}
